
import { Dealer } from '@/types/dealer';
import { debugLog } from '@/utils/debugLogger';

/**
 * Simple utility to format dealers with guaranteed numeric values
 */
export const formatDealersData = (
  dealers: any[],
  selectedMetal: string = 'ORO'
): Dealer[] => {
  if (!dealers || !Array.isArray(dealers)) {
    debugLog('dealer', 'No dealers provided to formatDealersData', undefined, 'formatDealersUtils');
    return [];
  }
  
  // Log raw input for debugging with complete data for the first dealer
  if (dealers.length > 0) {
    debugLog('dealer', 'COMPLETE RAW DEALER EXAMPLE:', dealers[0], 'formatDealersUtils');
  }
  
  // Log raw input for debugging
  debugLog('dealer', 'formatDealersData Input (first 3 dealers):', dealers.slice(0, 3).map(d => ({
    id: d.id,
    name: d.name,
    rating: d.rating,
    rating_type: typeof d.rating,
    rating_raw: d.rating,
    buySpread: d.buySpread,
    buySpread_type: typeof d.buySpread,
    buySpread_raw: d.buySpread
  })), 'formatDealersUtils');
  
  const formattedDealers = dealers
    .filter(dealer => dealer && dealer.id)
    .map(dealer => {
      // CRITICAL: Simply use the rating that's passed in
      // DON'T transform or overwrite with anything else
      // We expect this to already be properly processed from the source
      let rating = dealer.rating;
      
      // Only if rating is not a number, try to convert it
      if (typeof rating !== 'number') {
        // If it's a string, try to parse it
        if (typeof rating === 'string') {
          const parsed = parseFloat(rating);
          rating = isNaN(parsed) ? 0 : parsed;
        } else {
          // Fallback to 0 for null/undefined/other types
          rating = 0;
        }
      }
      
      // Process buySpread - PRESERVE the original value, including negative values
      // This is crucial for dealer price calculation
      let buySpread = dealer.buySpread;
      
      // Only try to convert string values to numbers, preserve null/undefined as is
      if (typeof buySpread === 'string') {
        const parsed = parseFloat(buySpread);
        buySpread = isNaN(parsed) ? null : parsed;
      }
      
      // Log processing for this dealer
      debugLog('pricing', `Processing dealer ${dealer.name}: buySpread INCOMING=${dealer.buySpread} (${typeof dealer.buySpread}), PROCESSED=${buySpread} (${typeof buySpread})`, undefined, 'formatDealersUtils');
      
      const formattedDealer: Dealer = {
        id: dealer.id,
        name: dealer.name,
        address: dealer.address,
        city: dealer.city || '',
        is_verified: !!dealer.activated,
        activated: !!dealer.activated,
        latitude: dealer.latitude,
        longitude: dealer.longitude,
        rating: rating,
        buySpread: buySpread, // Keep original value (including negative)
        isFavorite: false,
        phone: dealer.phone,
        // email rimossa per privacy - non esporre in SSR pubblico
        postal_code: dealer.postal_code,
        image_url: dealer.image_url,
        opening_hours: dealer.opening_hours,
        oam_verified: dealer.oam_verified || false,
        oam_code: dealer.oam_code || undefined,
        opo_code: dealer.opo_code || undefined,
        opo_verified: dealer.opo_verified || false,
        opo_registered: dealer.opo_verified || !!dealer.opo_code
      };
      
      return formattedDealer;
    });
  
  // Log output for debugging
  debugLog('dealer', 'formatDealersData Output (first 3 dealers):', formattedDealers.slice(0, 3).map(d => ({
    id: d.id,
    name: d.name,
    rating: d.rating,
    rating_type: typeof d.rating,
    buySpread: d.buySpread,
    buySpread_type: typeof d.buySpread
  })), 'formatDealersUtils');
  
  return formattedDealers;
};
